import {
  Box,
  Grid,
  TextField,
  Typography,
  Divider,
  useTheme,
  Paper,
  InputAdornment,
  Chip,
  Stack,
  useMediaQuery
} from "@mui/material";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import DiscountIcon from "@mui/icons-material/Discount";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import ReceiptIcon from "@mui/icons-material/Receipt";

interface StockReceiptItem {
  variant_id: number;
  stock_type: "in_store" | "online";
  quantity: number;
  unit_cost: number;
}

interface Props {
  items: StockReceiptItem[];
  discountAmount: number;
  shippingCost: number;
  importDuty: number;
  setDiscountAmount: (v: number) => void;
  setShippingCost: (v: number) => void;
  setImportDuty: (v: number) => void;
}

const CostSummary = ({
  items,
  discountAmount,
  shippingCost,
  importDuty,
  setDiscountAmount,
  setShippingCost,
  setImportDuty,
}: Props) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const subtotal = items.reduce(
    (sum, item) => sum + item.unit_cost * item.quantity,
    0
  );
  const total = subtotal + shippingCost + importDuty - discountAmount;

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} mb={2}>
        <ReceiptIcon color="primary" />
        <Typography variant="h6">Cost Summary</Typography>
      </Stack>
      
      <Grid container spacing={isMobile ? 1 : 2}>
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            label="Subtotal"
            value={subtotal.toFixed(2)}
            InputProps={{
              readOnly: true,
              startAdornment: (
                <InputAdornment position="start">
                  <AttachMoneyIcon color="action" />
                </InputAdornment>
              ),
              sx: { 
                backgroundColor: theme.palette.grey[50],
              }
            }}
            fullWidth
            size={isMobile ? "small" : "medium"}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <TextField
            label="Discount"
            type="number"
            value={discountAmount}
            onChange={(e) => setDiscountAmount(Number(e.target.value))}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <DiscountIcon color="action" />
                </InputAdornment>
              ),
              inputProps: { min: 0 }
            }}
            fullWidth
            size={isMobile ? "small" : "medium"}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <TextField
            label="Shipping"
            type="number"
            value={shippingCost}
            onChange={(e) => setShippingCost(Number(e.target.value))}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LocalShippingIcon color="action" />
                </InputAdornment>
              ),
              inputProps: { min: 0 }
            }}
            fullWidth
            size={isMobile ? "small" : "medium"}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <TextField
            label="Duty"
            type="number"
            value={importDuty}
            onChange={(e) => setImportDuty(Number(e.target.value))}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <AccountBalanceIcon color="action" />
                </InputAdornment>
              ),
              inputProps: { min: 0 }
            }}
            fullWidth
            size={isMobile ? "small" : "medium"}
          />
        </Grid>

        <Grid item xs={12}>
          <Divider sx={{ my: 2 }} />
        </Grid>

        <Grid item xs={12}>
          <TextField
            label="Total Amount"
            value={total.toFixed(2)}
            InputProps={{
              readOnly: true,
              startAdornment: (
                <InputAdornment position="start">
                  <AttachMoneyIcon color="primary" />
                </InputAdornment>
              ),
              sx: { 
                backgroundColor: theme.palette.primary.light,
                color: theme.palette.primary.contrastText,
                fontWeight: 'bold',
              }
            }}
            fullWidth
            size={isMobile ? "small" : "medium"}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default CostSummary;